import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Grid, styled, TextField, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Box, IconButton, Chip, Tooltip, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getStyles } from './styles';
import { BasicUserInfo, SiteDTO, TransformAddUserDTO, AddUserTransformSaveRequest } from '@beable/roster-backend-client-v1';
import { Autocomplete } from '@mui/material';
import { uniqueId, debounce } from 'lodash';
import transformService from '../../services/TransformService';
import SuccessDialog from './SuccessDialog';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';

const Form = styled('form')(({ theme }) => ({
    width: '100%',
    marginTop: theme.spacing(1),
}));

type Option = {
    value: string;
    label: string;
};

interface AdhocUserData {
    orgs: Option[];
    role: string;
    username: string;
    userIds?: string;
    givenName: string;
    familyName: string;
    middleName?: string;
    identifier?: string;
    email?: string;
    sms?: string;
    phone?: string;
    agentSourcedIds?: string;
    grades?: string;
    password: string;
    primaryLanguage: string;
}

const defaultState: AdhocUserData = {
    orgs: [],
    role: '',
    username: '',
    userIds: '',
    givenName: '',
    familyName: '',
    middleName: '',
    identifier: '',
    email: '',
    sms: '',
    phone: '',
    agentSourcedIds: '',
    grades: '',
    password: '',
    primaryLanguage: ''
};

const roleOptions: Option[] = [
    { value: 'teacher', label: 'Teacher' },
    { value: 'student', label: 'Student' },
    { value: 'administrator', label: 'Admin' },
];

const languageOptions: Option[] = [{ value: 'EN', label: 'English' }, { value: 'ES', label: 'Spanish' }];

const usernameValidationErrorMessage = <>Username cannot include spaces. <br /> Username cannot exist for a different user in this site.</>;

const gradeOptions: Option[] = [
    { value: 'K', label: 'K' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
    { value: '7', label: '7' },
    { value: '8', label: '8' },
    { value: '9', label: '9' },
    { value: '10', label: '10' },
    { value: '11', label: '11' },
    { value: '12', label: '12' }
];

const editableFields = [
    { value: 'username', label: 'Username' },
    { value: 'givenName', label: 'First Name' },
    { value: 'familyName', label: 'Last Name' },
    { value: 'middleName', label: 'Middle Name' },
    { value: 'email', label: 'Email' },
    { value: 'role', label: 'Role' },
    { value: 'grades', label: 'Grade' },
    { value: 'orgSourcedIds', label: 'Orgs' },
    { value: 'userIds', label: 'User IDs' },
    { value: 'identifier', label: 'Identifier' },
    { value: 'sms', label: 'SMS' },
    { value: 'phone', label: 'Phone' },
    { value: 'agentSourcedIds', label: 'Agent Sourced IDs' },
    { value: 'beablePrimaryLanguage', label: 'Primary Language' },
    { value: 'password', label: 'Password' },
] as const satisfies readonly Option[];

type EditableFieldName = typeof editableFields[number]['value'];

const AutoCompleteFieldValues = ({
    value,
    onChange,
    options,
}: {
    value: string;
    onChange: (value: string) => void;
    options: Option[];
}) => {
    const renderNewValueInput = (params: any) => (
        <TextField
            {...params}
            label="New Value"
            size="small"
            required
            sx={{ mb: 2 }}
        />
    );

    return (
        <Autocomplete
            fullWidth
            options={options}
            value={options.find(option => option.value === value) || null}
            onChange={(_event, option) => onChange(option?.value || '')}
            renderInput={renderNewValueInput}
        />
    );
};

interface UsersTransformProps {
    site: SiteDTO | undefined;
    isSiteSelected: boolean;
}

export default function UsersTransform({ site, isSiteSelected }: UsersTransformProps) {
    const theme = useTheme();
    const styles = getStyles(theme);

    const [adhocUser, setAdhocUser] = useState<AdhocUserData>(defaultState);
    const {
        orgs,
        role,
        username,
        userIds,
        givenName,
        familyName,
        middleName,
        identifier,
        email,
        sms,
        phone,
        agentSourcedIds,
        grades,
        password,
        primaryLanguage
    } = adhocUser;

    const [schoolOptions, setSchoolOptions] = useState<Option[]>([]);
    const [existingRecords, setExistingRecords] = useState<TransformAddUserDTO[]>([]);
    const [loadingRecords, setLoadingRecords] = useState(false);

    const [usernameError, setUsernameError] = useState(false);
    const [showOptionalFields, setShowOptionalFields] = useState(false);

    const [formSubmitting, setFormSubmitting] = useState(false);
    const [forceReset, setForceReset] = useState('');
    const [dialog, showDialog] = useState(false);
    const [successSaving, setSuccessSaving] = useState(false);
    const [dialogAction, setDialogAction] = useState<'save' | 'update'>('save');
    const [expandedRecord, setExpandedRecord] = useState<string | null>(null);
    const [userToUpdate, setUserToUpdate] = useState<string | null>(null);
    const [selectedField, setSelectedField] = useState<EditableFieldName | ''>(''); 
    const [updatedFieldValue, setUpdatedFieldValue] = useState<string>('');
    const [existingFieldValue, setExistingFieldValue] = useState<string>('');
    const [savingEdit, setSavingEdit] = useState(false);
    const [usernameUpdateError, setUsernameUpdateError] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState<string>('');
    useEffect(() => {
        handleResetForm();
        const getData = async (siteId: number) => {
            await getSchools(siteId);
            await fetchExistingRecords(siteId);
        }
        if (site?.id) { getData(site.id); }
        else {
            setSchoolOptions([]);
            setExistingRecords([]);
        }
    }, [site]);

    const getSchools = async (siteId: number) => {
        const data = await transformService.getOrgs(siteId);
        if (!data) {
            setSchoolOptions([]);
            return;
        }
        setSchoolOptions(data.map((school) => ({
            value: school.externalSourceId,
            label: school.name
        })));
    }

    const fetchExistingRecords = async (siteId: number) => {
        setLoadingRecords(true);
        const data = await transformService.getTransformAddUsers(siteId);

        if (data) {
            setExistingRecords(data);
        } else {
            setExistingRecords([]);
        }
        setLoadingRecords(false);
    }


    const isUsernameValid = (
        usersList?: BasicUserInfo[],
        passedUsername?: string,
        isUpdate?: boolean,
        currentUserSourcedId?: string
    ) => {
        const setErrorFn = isUpdate ? setUsernameUpdateError : setUsernameError;
        const username = passedUsername ?? adhocUser.username;
        if (username.indexOf(' ') !== -1) {
            setErrorFn(true);
            return false;
        }
        const usersToCheck = isUpdate && currentUserSourcedId && usersList
            ? usersList.filter(u => u.externalSourceId !== currentUserSourcedId)
            : usersList;
        if (usersToCheck && !!usersToCheck.find(u => u.username.toLowerCase() === username.toLowerCase())) {
            setErrorFn(true);
            return false;
        }
        setErrorFn(false);
        return true;
    };

    const handleInputChange = (prop: keyof AdhocUserData, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setAdhocUser({ ...adhocUser, [prop]: event.target.value });
    }

    const handleSelectChange = (prop: keyof AdhocUserData, value: any) => {
        setAdhocUser({ ...adhocUser, [prop]: value });
    };
    const isUsernameValidRef = useRef(isUsernameValid);
    isUsernameValidRef.current = isUsernameValid;

    const debouncedUsernameApiValidation = useMemo(
        () => debounce(
            async (siteId: number, username: string, isUpdate: boolean, currentUserSourcedId?: string) => {
                const usersList = await transformService.getUsersBySubstring(siteId, username);
                isUsernameValidRef.current(usersList || [], username, isUpdate, currentUserSourcedId);
            },
            300
        ),
        []
    );

    const runUsernameValidation = (username: string, isUpdate: boolean, currentUserSourcedId?: string) => {
        isUsernameValid(undefined, username, isUpdate);
        if (username.indexOf(' ') !== -1 || !username.trim()) return;
        if (!site?.id) return;
        debouncedUsernameApiValidation(site.id, username, isUpdate, currentUserSourcedId);
    };

    useEffect(() => {
        if (!site?.id) return;

        if (userToUpdate && selectedField === 'username' && updatedFieldValue) {
            runUsernameValidation(updatedFieldValue, true, existingRecords.find(r => r.id === userToUpdate)?.sourcedId);
            return () => debouncedUsernameApiValidation.cancel();
        }

        if (!userToUpdate && adhocUser.username) {
            runUsernameValidation(adhocUser.username, false);
            return () => debouncedUsernameApiValidation.cancel();
        }
    }, [updatedFieldValue, selectedField, userToUpdate, site?.id, existingRecords, adhocUser.username]);

    const handleResetForm = () => {
        setAdhocUser(defaultState);
        setForceReset(uniqueId());
    };

    const isFormValid = useMemo(() => {
        if (!isUsernameValid()) {
            return false;
        }
        if (adhocUser.username === '') {
            return false;
        }
        if (adhocUser.password === '') {
            return false;
        }
        if (adhocUser.givenName === '') {
            return false;
        }
        if (adhocUser.familyName === '') {
            return false;
        }
        if (!adhocUser.orgs?.length) {
            return false;
        }
        if (adhocUser.primaryLanguage === '' || !adhocUser.primaryLanguage) {
            return false;
        }
        if (adhocUser.role === '' || !adhocUser.role) {
            return false;
        }
        if (adhocUser.role === 'student' && (adhocUser.grades === '' || !adhocUser.grades)) {
            return false;
        }
        return true;
    }, [adhocUser]);

    const handleSaveForm = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setFormSubmitting(true);

        let orgIds: string = '';
        orgs.forEach(o => {
            orgIds = orgIds + (orgIds === '') ? `${o.value}` : `,${o.value}`;
        })

        const user: AddUserTransformSaveRequest = {
            siteId: site?.id ?? 0,
            orgIds,
            role,
            username,
            userIds,
            givenName,
            familyName,
            middleName,
            identifier,
            email,
            sms,
            phone,
            agentSourcedIds,
            grades,
            password,
            beablePrimaryLanguage: primaryLanguage,
        };

        const response = await transformService.saveAddUserTransformation(user);

        setFormSubmitting(false);
        setSuccessSaving(true)

        if (!response) {
            setSuccessSaving(false);
        }
        setDialogAction('save');
        showDialog(true);
    };

    const closeDialog = () => {
        setSuccessSaving(false)
        showDialog(false);
        if (dialogAction === 'save') {
            handleResetForm();
        }
        if (site?.id) {
            fetchExistingRecords(site.id);
        }
    };

    const handleViewRecord = (record: TransformAddUserDTO) => {
        if (expandedRecord === record.id) {
            // If already expanded, hide it
            setExpandedRecord(null);
        } else {
            // If not expanded, show it
            setExpandedRecord(record.id || null);
        }
    };

    const resetEditState = () => {
        setUserToUpdate(null);
        setSelectedField('');
        setUpdatedFieldValue('');
        setExistingFieldValue('');
        setUsernameUpdateError(false);
    };

    const handleUpdateRecord = (record: TransformAddUserDTO) => {
        if (userToUpdate === record.id) {
            resetEditState();
        } else {
            setUserToUpdate(record.id || null);
            setSelectedField('');
            setUpdatedFieldValue('');
            if (expandedRecord === record.id) {
                setExpandedRecord(null);
            }
        }
    };

    const handleSaveEdit = async (record: TransformAddUserDTO) => {
        if (!record.id || !site?.id || !selectedField || !updatedFieldValue?.trim()) return;

        if (selectedField === 'username') {
            const usersList = await transformService.getUsersBySubstring(site.id, updatedFieldValue);
            if (!isUsernameValid(usersList || [], updatedFieldValue, true, record.sourcedId)) {
                return;
            }
        }

        setSavingEdit(true);

        try {
            const requestPayload = {
                columnName: selectedField,
                newValue: updatedFieldValue,
                siteId: site.id,
                id: record.id
            };
                        
            const response = await transformService.updateTransformAddUser(requestPayload);
            
            setSavingEdit(false);
            
            if (response) {
                setSuccessSaving(true);
                setDialogAction('update');
                showDialog(true);
                resetEditState();
                await fetchExistingRecords(site.id);
            } else {
                setSuccessSaving(false);
                setDialogAction('update');
                showDialog(true);
                console.error('Failed to update transform add user - response was null/false');
            }
        } catch (error) {
            setSavingEdit(false);
            setSuccessSaving(false);
            setDialogAction('update');
            showDialog(true);
            console.error('Error updating transform add user:', error);
        }
    };


    const handleCancelEditForm = () => {
        resetEditState();
    };

    const normalizeOrgIdsForComparison = (orgIds: string) =>
        orgIds.split(',').map(s => s.trim()).filter(Boolean).sort().join(',');

    const isOrgsValueChanged = (newVal: string, existingVal: string) =>
        normalizeOrgIdsForComparison(newVal) !== normalizeOrgIdsForComparison(existingVal);

    const hasFieldValueChanged = selectedField === 'orgSourcedIds'
        ? isOrgsValueChanged(updatedFieldValue, existingFieldValue)
        : updatedFieldValue !== existingFieldValue;

    const fieldOptionsMap: Partial<Record<EditableFieldName, Option[]>> = useMemo(() => ({
        role: roleOptions,
        grades: gradeOptions,
        beablePrimaryLanguage: languageOptions,
        orgSourcedIds: schoolOptions,
    }), [schoolOptions]);

    const filteredRecords = useMemo(() => {
        return existingRecords.filter(record => {
            const matchesSearch = !searchTerm || 
                record.sourcedId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.givenName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.familyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                record.email?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesRole = !filterRole || record.role === filterRole;
            
            return matchesSearch && matchesRole;
        });
    }, [existingRecords, searchTerm, filterRole]);

    return (
        <>
            {dialog === true && <SuccessDialog 
                closeDialog={closeDialog} 
                success={successSaving} 
                action={dialogAction}
            />}
            <Grid size={12}>
                    <Grid container>
                        <Grid
                            sx={{ display: "flex", justifyContent: "flex-start", marginTop: '6px' }}
                            size={12}>
                            <Typography variant="h5" align-left='true'>
                                Add transform users for the selected site.
                            </Typography>
                        </Grid>
                        <Grid sx={{ display: "flex", justifyContent: "flex-start" }} size={12}>
                            <p align-left='true'>
                                Fill out and save the following form. This will add records to transformAddUsers. <br />
                                To apply these transformations to the site, roster a batch. Once completed, the users will be added to the roster data.
                            </p>
                        </Grid>
                    </Grid>
                <Form onSubmit={handleSaveForm}>
                    <Grid sx={styles.formContainer}>
                        <Grid sx={styles.doubleInput}>
                            <TextField
                                sx={styles.flexAuto}
                                required
                                variant='outlined'
                                name='userName'
                                value={username}
                                label='Username'
                                data-testid='username'
                                disabled={!isSiteSelected}
                                error={usernameError}
                                helperText={usernameError ? usernameValidationErrorMessage : undefined}
                                onChange={(e) => handleInputChange('username', e)}
                            />
                            <TextField
                                sx={styles.flexAuto}
                                required
                                variant='outlined'
                                label='Password'
                                name='password'
                                data-testid='password'
                                value={password}
                                disabled={!isSiteSelected}
                                onChange={(e) => handleInputChange('password', e)}
                            />
                        </Grid>
                        <Grid sx={styles.doubleInput}>
                            <TextField
                                sx={styles.flexAuto}
                                required
                                variant='outlined'
                                label='First Name'
                                name='givenName'
                                data-testid='givenName'
                                value={givenName}
                                disabled={!isSiteSelected}
                                onChange={(e) => handleInputChange('givenName', e)}
                            />
                            <TextField
                                sx={styles.flexAuto}
                                required
                                variant='outlined'
                                label='Last Name'
                                name='familyName'
                                data-testid='familyName'
                                value={familyName}
                                disabled={!isSiteSelected}
                                onChange={(e) => handleInputChange('familyName', e)}
                        />
                        </Grid>
                        <Grid sx={styles.doubleInput}>
                            <TextField
                                sx={styles.flexAuto}
                                variant='outlined'
                                label='Middle Name'
                                name='middleName'
                                data-testid='middleName'
                                value={middleName}
                                disabled={!isSiteSelected}
                                onChange={(e) => handleInputChange('middleName', e)}
                            />
                            <TextField
                                sx={styles.flexAuto}
                                variant='outlined'
                                label='Email'
                                name='email'
                                data-testid='email'
                                value={email}
                                disabled={!isSiteSelected}
                                onChange={(e) => handleInputChange('email', e)}
                            />
                        </Grid>
                        <Grid sx={styles.doubleInput}>
                            <Autocomplete
                                sx={styles.flexAuto}
                                multiple={true}
                                filterSelectedOptions={true}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                id={'orgs'}
                                options={schoolOptions}
                                data-testid='orgs'
                                aria-label='orgs-dropdown'
                                getOptionLabel={(option) => option.label}
                                disabled={!isSiteSelected}
                                onChange={(_event, value) => handleSelectChange('orgs', value)}
                                disableCloseOnSelect
                                value={orgs}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        label='Orgs'
                                        required={true}
                                        slotProps={{
                                            ...params.slotProps,

                                            htmlInput: {
                                                ...params.slotProps.htmlInput,
                                                required: orgs?.length === 0,

                                            }
                                        }}
                                    />
                                )}
                            />
                            <Autocomplete
                                sx={styles.flexAuto}
                                key={forceReset}
                                multiple={false}
                                filterSelectedOptions={false}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                id={'language'}
                                aria-label='language-dropdown'
                                options={languageOptions}
                                getOptionLabel={(option) => option.label}
                                disabled={!isSiteSelected}
                                onChange={(_event, value) => handleSelectChange('primaryLanguage', value?.value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        label='Primary Language'
                                        required={true}
                                        slotProps={{
                                            ...params.slotProps,

                                            htmlInput: {
                                                ...params.slotProps.htmlInput,
                                                required: primaryLanguage === ''
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid sx={styles.doubleInput}>
                            <Autocomplete
                                sx={styles.flexAuto}
                                value={{ value: role, label: role }}
                                multiple={false}
                                filterSelectedOptions={false}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                id={'role'}
                                aria-label='role-dropdown'
                                options={roleOptions}
                                getOptionLabel={(option) => option.label}
                                disabled={!isSiteSelected}
                                onChange={(_event, value) => handleSelectChange('role', value?.value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        label='Role'
                                        required={true}
                                        slotProps={{
                                            ...params.slotProps,

                                            htmlInput: {
                                                ...params.slotProps.htmlInput,
                                                required: role === ''
                                            }
                                        }}
                                    />
                                )}
                            />
                            {role === 'student' &&
                                <Autocomplete
                                    sx={styles.flexAuto}
                                    key={forceReset}
                                    multiple={false}
                                    filterSelectedOptions={false}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    id={'grade'}
                                    aria-label='grade-dropdown'
                                    options={gradeOptions}
                                    getOptionLabel={(option) => option.label}
                                    disabled={!isSiteSelected}
                                    onChange={(_event, value) => handleSelectChange('grades', value?.value)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant='outlined'
                                            label='Grade'
                                            required={true}
                                            slotProps={{
                                                ...params.slotProps,

                                                htmlInput: {
                                                    ...params.slotProps.htmlInput,
                                                    required: grades === ''
                                                }
                                            }}
                                        />
                                    )}
                                />
                            }
                        </Grid>
                        <Grid onClick={() => setShowOptionalFields(o => !o)}>
                            <Button size="small" startIcon={showOptionalFields ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />} >
                                View additional optional fields. (User ids, identifier, sms, phone and agent sourced id)
                            </Button>
                        </Grid>
                        {showOptionalFields && <>
                            <Grid sx={styles.doubleInput}>
                                <TextField
                                    sx={styles.flexAuto}
                                    variant='outlined'
                                    label='Identifier'
                                    name='identifier'
                                    data-testid='identifier'
                                    value={identifier}
                                    disabled={!isSiteSelected}
                                    onChange={(e) => handleInputChange('identifier', e)}
                                />
                            </Grid>
                            <Grid sx={styles.doubleInput}>
                                <TextField
                                    sx={styles.flexAuto}
                                    variant='outlined'
                                    label='SMS'
                                    name='sms'
                                    data-testid='sms'
                                    value={sms}
                                    disabled={!isSiteSelected}
                                    onChange={(e) => handleInputChange('sms', e)}
                                />
                                <TextField
                                    sx={styles.flexAuto}
                                    variant='outlined'
                                    label='Phone'
                                    name='phone'
                                    data-testid='phone'
                                    value={phone}
                                    disabled={!isSiteSelected}
                                    onChange={(e) => handleInputChange('phone', e)}
                                />
                            </Grid>
                            <Grid sx={styles.doubleInput}>
                                <TextField
                                    sx={styles.flexAuto}
                                    variant='outlined'
                                    label='User Ids'
                                    name='userIds'
                                    data-testid='userIds'
                                    value={userIds}
                                    disabled={!isSiteSelected}
                                    onChange={(e) => handleInputChange('userIds', e)}
                                />
                                <TextField
                                    sx={styles.flexAuto}
                                    variant='outlined'
                                    label='Agent Sourced Ids'
                                    name='agentSourcedIds'
                                    data-testid='agentSourcedIds'
                                    value={agentSourcedIds}
                                    disabled={!isSiteSelected}
                                    onChange={(e) => handleInputChange('agentSourcedIds', e)}
                                />
                            </Grid>
                        </>}
                        <Grid sx={styles.formButtons}>
                            <Button onClick={handleResetForm} sx={styles.saveForm}>
                                Cancel
                            </Button>
                            <Button
                                color='primary'
                                sx={styles.saveForm}
                                type='submit'
                                disabled={!isFormValid || formSubmitting}
                                variant='contained'
                                aria-label='save-button'
                            >
                                Save
                            </Button>
                        </Grid>
                    </Grid>
                </Form>
                
                {/* Existing Records Section */}
                <Grid sx={{ marginTop: '32px' }} size={12}>
                    <Typography variant="h6" sx={{ marginBottom: '16px' }}>
                        Existing Transform User Records ({filteredRecords.length})
                    </Typography>
                    
                    {/* Search and Filter Controls */}
                    <Grid container spacing={2} sx={{ marginBottom: '16px' }}>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 6
                            }}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                label="Search records"
                                placeholder="Search by username, name, or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                size="small"
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 6
                            }}>
                            <Autocomplete
                                fullWidth
                                options={roleOptions}
                                value={roleOptions.find(option => option.value === filterRole) || null}
                                onChange={(_event, value) => setFilterRole(value?.value || '')}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Filter by role"
                                        size="small"
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                    {loadingRecords ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                p: 3
                            }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Sourced ID</TableCell>
                                        <TableCell>Username</TableCell>
                                        <TableCell>Role</TableCell>
                                        <TableCell>Grades</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRecords.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                {existingRecords.length === 0 
                                                    ? 'No existing transform user records found'
                                                    : 'No records match the current search criteria'
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredRecords.map((record, index) => (
                                            <React.Fragment key={record.id || index}>
                                                <TableRow>
                                                    <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.sourcedId}</TableCell>
                                                    <TableCell>{record.username}</TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={record.role} 
                                                            color={record.role === 'student' ? 'primary' : record.role === 'teacher' ? 'secondary' : 'default'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>{record.grades || '-'}</TableCell>
                                                    <TableCell>
                                                        <Tooltip title={expandedRecord === record.id ? "Hide Details" : "View Details"}>
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => handleViewRecord(record)}
                                                                disabled={userToUpdate === record.id}
                                                            >
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={userToUpdate === record.id ? "Cancel Edit" : "Edit"}>
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => handleUpdateRecord(record)}
                                                                disabled={expandedRecord === record.id}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                                {expandedRecord === record.id && (
                                                    <TableRow>
                                                        <TableCell colSpan={6}>
                                                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                                                <Typography variant="h5" sx={{ mb: 2 }}>
                                                                    View Details - <Box component="span" sx={{ fontSize: '0.75em', fontWeight: 400, display: 'inline-block', verticalAlign: 'middle' }}>{record.givenName} {record.familyName}</Box>
                                                                </Typography>
                                                                <Grid container spacing={2}>
                                                                    <Grid
                                                                        size={{
                                                                            xs: 12,
                                                                            sm: 6
                                                                        }}>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>ID:</strong> {record.id}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Site ID:</strong> {record.siteId}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Sourced ID:</strong> {record.sourcedId}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Username:</strong> {record.username || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Given Name:</strong> {record.givenName || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Family Name:</strong> {record.familyName || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Middle Name:</strong> {record.middleName || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Email:</strong> {record.email || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Role:</strong> {record.role || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Grades:</strong> {record.grades || ''}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid
                                                                        size={{
                                                                            xs: 12,
                                                                            sm: 6
                                                                        }}>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Status:</strong> {record.status || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Enabled User:</strong> {record.enabledUser || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Org Sourced IDs:</strong> {record.orgSourcedIds || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>User IDs:</strong> {record.userIds || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Identifier:</strong> {record.identifier || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>SMS:</strong> {record.sms || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Phone:</strong> {record.phone || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Agent Sourced IDs:</strong> {record.agentSourcedIds || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                            <strong>Primary Language:</strong> {record.beablePrimaryLanguage || ''}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1, wordBreak: 'break-all' }}>
                                                                            <strong>Password:</strong> {record.password || ''}
                                                                        </Typography>
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {userToUpdate === record.id && (
                                                    <TableRow>
                                                        <TableCell colSpan={6}>
                                                            <Box sx={{ p: 2, bgcolor: 'grey.100' }}> 
                                                                <Typography variant="h5" sx={{ mb: 2 }}>
                                                                    Edit Record - <Box component="span" sx={{ fontSize: '0.75em', fontWeight: 400, display: 'inline-block', verticalAlign: 'middle' }}>{record.givenName} {record.familyName}</Box>
                                                                </Typography>
                                                                <Grid container spacing={2}>
                                                                    <Grid
                                                                        size={{
                                                                            xs: 12,
                                                                            sm: 6
                                                                        }}>
                                                                        <Autocomplete
                                                                            fullWidth
                                                                            options={editableFields}
                                                                            value={selectedField ? editableFields.find(field => field.value === selectedField) || null : null}
                                                                            onChange={(_event, value) => {
                                                                                setSelectedField(value?.value || '');
                                                                                setUsernameUpdateError(false);
                                                                                if (value?.value && record) {
                                                                                    const currentValue = (record as any)[value.value] || '';
                                                                                    setExistingFieldValue(String(currentValue));
                                                                                    setUpdatedFieldValue('');
                                                                                } else {
                                                                                    setExistingFieldValue('');
                                                                                    setUpdatedFieldValue('');
                                                                                }
                                                                            }}
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    label="Select Field"
                                                                                    size="small"
                                                                                    required
                                                                                    sx={{ mb: 2 }}
                                                                                />
                                                                            )}
                                                                        />
                                                                    </Grid>
                                                                    {selectedField && (    
                                                                        <Grid
                                                                            size={{
                                                                                xs: 12,
                                                                                sm: 6
                                                                            }}>
                                                                            {selectedField === 'orgSourcedIds' ? (
                                                                                <Autocomplete
                                                                                    fullWidth
                                                                                    multiple
                                                                                    filterSelectedOptions
                                                                                    disableCloseOnSelect
                                                                                    options={schoolOptions}
                                                                                    getOptionLabel={(option) => option.label}
                                                                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                                                                    value={updatedFieldValue
                                                                                        ? updatedFieldValue.split(',').map(v => schoolOptions.find(o => o.value === v.trim())).filter(Boolean) as Option[]
                                                                                        : []}
                                                                                    onChange={(_event, selectedOptions) => {
                                                                                        setUpdatedFieldValue(selectedOptions.map(o => o.value).join(','));
                                                                                    }}
                                                                                    renderInput={(params) => (
                                                                                        <TextField
                                                                                            {...params}
                                                                                            label="New Value"
                                                                                            size="small"
                                                                                            required
                                                                                            sx={{ mb: 2 }}
                                                                                        />
                                                                                    )}
                                                                                />
                                                                            ) : fieldOptionsMap[selectedField] ? (
                                                                                <AutoCompleteFieldValues
                                                                                    value={updatedFieldValue}
                                                                                    onChange={setUpdatedFieldValue}
                                                                                    options={fieldOptionsMap[selectedField]}
                                                                                />
                                                                            ) : (
                                                                                <TextField
                                                                                    fullWidth
                                                                                    label="New Value"
                                                                                    value={updatedFieldValue}
                                                                                    onChange={(e) => setUpdatedFieldValue(e.target.value)}
                                                                                    size="small"
                                                                                    required
                                                                                    type="text"
                                                                                    error={selectedField === 'username' && usernameUpdateError}
                                                                                    helperText={selectedField === 'username' && usernameUpdateError ? usernameValidationErrorMessage : undefined}
                                                                                    sx={{ mb: 2 }}
                                                                                />
                                                                            )}
                                                                        </Grid>
                                                                    )}
                                                                    {selectedField && updatedFieldValue?.trim() && hasFieldValueChanged && (
                                                                        <Grid size={12}>
                                                                            <Divider>
                                                                            <Chip
                                                                                label={
                                                                                    <>
                                                                                        The transformed user's <strong>{editableFields.find(f => f.value === selectedField)?.label || selectedField}</strong> will be updated
                                                                                        {selectedField !== 'password' && <> from <strong>{existingFieldValue || '(empty)'}</strong></>}
                                                                                        {' '}to <strong>{updatedFieldValue || '(empty)'}</strong>.
                                                                                    </>
                                                                                }
                                                                            />
                                                                            </Divider>
                                                                        </Grid>
                                                                    )}
                                                                </Grid>
                                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                                                                    <Button 
                                                                        onClick={handleCancelEditForm}
                                                                        sx={styles.saveForm}
                                                                        disabled={savingEdit}
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                    <Button
                                                                        color='primary'
                                                                        sx={styles.saveForm}
                                                                        onClick={() => handleSaveEdit(record)}
                                                                        disabled={savingEdit || !selectedField || !updatedFieldValue?.trim() || !hasFieldValueChanged || (selectedField === 'username' && usernameUpdateError)}
                                                                        variant='contained'
                                                                    >
                                                                        {savingEdit ? 'Saving...' : 'Save'}
                                                                    </Button>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Grid>
                
            </Grid >
        </>
    );
}
